import { Css } from "@homebound/beam";
import { useCreateAssetMutation } from "src/components/uploader/DocumentUploader.generated";
import { useUploaderContext } from "src/contexts/UploaderContext";
import { DocumentEditorDetailFragment } from "src/fragments/circularFragments.generated";
import { Asset, AssetInput, DocumentType, SaveDocumentInput } from "src/generated/graphqlTypes";
import { useSaveDocumentsMutation } from "src/routes/projects/documents/DocumentsPage.generated";
import { UppyUploader } from "./UppyUploader";

// Define only the required fields for creating the asset without being attached to a document/projectId
export type FileAsset = Pick<Asset, "id" | "attachmentUrl" | "contentType" | "createdAt" | "downloadUrl" | "version">;

export type DocumentUploaderV2Props = {
  documentType: DocumentType;
  file: DocumentEditorDetailFragment | undefined;
  onFinish: (file: DocumentEditorDetailFragment | Asset["id"] | undefined, asset?: FileAsset) => void;
  projectId?: string;
  onUploadStart?: () => void;
  message?: string;
  readOnly?: boolean;
  error?: string;
  onClear?: () => void;
  multiple?: boolean;
  xss?: Record<string, unknown>;
};

/**
 * Document-specific file uploader that uploads files via UppyUploader and creates
 * documents via `saveDocuments` (when `projectId` is provided) or standalone assets
 * via `createAsset` (when no `projectId` is available, e.g. bill OCR).
 *
 * When uploading multiple documents, `onFinish` is called once for each file uploaded.
 *
 * Usage:
 * - Only consumed by `BillEditor` (src/routes/bills/BillEditor.tsx) for bill document
 *   uploads with optional OCR processing.
 */
export function DocumentUploaderV2(props: DocumentUploaderV2Props) {
  const { documentType, projectId, onFinish, file, message, readOnly, multiple, xss } = props;
  const { setFiles } = useUploaderContext();

  const [saveDocuments] = useSaveDocumentsMutation();
  const [createAsset] = useCreateAssetMutation();

  if (readOnly) {
    return <div data-testid="read-only-file-uploader">{file ? file.name : "No files uploaded"}</div>;
  }

  // If a file is already uploaded, show it with a clear option
  if (file) {
    return (
      <div data-testid="uploaded-file">
        <span>{file.name}</span>
        {props.onClear && (
          <button data-testid="file-uploader-clear" onClick={props.onClear}>
            Clear
          </button>
        )}
      </div>
    );
  }

  return (
    <div css={{ ...Css.$, ...xss }}>
      <UppyUploader
        type="DragDrop"
        allowedFileTypes={["application/pdf"]}
        dragDropText={message ?? "Drag & drop files"}
        dragDropWidth="100%"
        dragDropHeight="100%"
        maxNumberOfFiles={multiple ? undefined : 1}
        errorMessage={props.error}
        onFinish={async (uppyFile) => {
          const { s3Key } = uppyFile.meta;
          const contentType = "application/pdf";

          props.onUploadStart?.();

          if (projectId) {
            // Upload a document to a project
            const input: SaveDocumentInput = {
              parentId: projectId,
              documentType,
              name: uppyFile.name,
              sizeInBytes: uppyFile.size ?? 0,
              asset: { contentType, fileName: uppyFile.name, s3Key, fileSizeInBytes: uppyFile.size ?? 0 },
            };
            const { data: documentData } = await saveDocuments({ variables: { input } });
            const document = documentData?.saveDocuments?.documents?.first;
            if (document) {
              setFiles({ [document.id]: { name: uppyFile.name, progress: 100 } });
            }
            onFinish(document);
          } else {
            // Upload an asset that will be attached to a document later (e.g. bill OCR)
            const input: AssetInput = {
              contentType,
              fileName: uppyFile.name,
              s3Key,
              fileSizeInBytes: uppyFile.size ?? 0,
            };
            const { data: asset } = await createAsset({ variables: { input } });
            const createdAsset = asset!.createAsset.asset;
            onFinish(createdAsset.id, createdAsset);
          }
        }}
      />
    </div>
  );
}
